import React, { RefObject, useMemo } from 'react';
import { IconActivityHeartbeat, IconArrowRight, IconCheck, IconLoader2, IconX } from '@tabler/icons-react';
import { ActionHistoryEntry, HistoryEntry, SmsLog } from '../../../entities/lead/model/types';
import { AssignmentsSection } from './AssignmentsSection';
import { CardModalAssignmentsController } from '../model/cardModalTypes';
import type { MoscowPrefill } from '../../pin-moscow-form';
import { formatShortDateTime, getManagerColor, getManagerInitials } from '../../../shared/lib/utils';
import { isAssignmentExpired } from '../model/useCardModalAssignments';
import { CardModalHistoryPane } from './CardModalHistoryPane';

interface Props {
  hidden: boolean;
  loading: boolean;
  history: HistoryEntry[];
  actions: ActionHistoryEntry[];
  smsLogs?: SmsLog[];
  assignments: CardModalAssignmentsController;
  pinPrefill?: MoscowPrefill;
  comment: string;
  onCommentChange: (value: string) => void;
  onAddComment: () => void;
  scrollRef: RefObject<HTMLDivElement | null>;
}

const getCommentAuthorName = (entry: HistoryEntry) => (
  entry.expand?.userId?.name || entry.expand?.userId?.email || 'Неизвестный'
);

const getCommentAuthorColor = (entry: HistoryEntry) => (
  getManagerColor(entry.expand?.userId?.id || entry.userId || getCommentAuthorName(entry))
);

const CardModalFeedPane: React.FC<Props> = ({
  hidden,
  loading,
  history,
  actions,
  smsLogs = [],
  assignments,
  pinPrefill,
  comment,
  onCommentChange,
  onAddComment,
  scrollRef,
}) => {
  const timeline = useMemo(() => [
    ...history.map(entry => ({ kind: 'comment' as const, entry, date: entry.created ?? '' })),
    ...assignments.completedAssignments.map(assignment => ({
      kind: 'task' as const,
      assignment,
      date: assignment.planDate ?? '',
    })),
  ].sort((a, b) => (a.date || '').localeCompare(b.date || '')), [assignments.completedAssignments, history]);

  return (
    <div className={`cm-l${hidden ? ' cm-tab-hidden' : ''}`} style={loading ? { visibility: 'hidden' } : undefined}>
      <div className="cm-l-col">
        <div className="cm-l-panel">
          <div className="cm-l-panel-scroll" ref={scrollRef}>
            <div className="amo-feed">
              {timeline.length === 0 && assignments.pendingAssignments.length === 0 && !assignments.isAssignmentLoading && (
                <div className="amo-feed-empty">Активности пока нет</div>
              )}
              {timeline.map(item => {
                if (item.kind === 'comment') {
                  const entry = item.entry;
                  return (
                    <div className="amo-feed-item" key={`c-${entry.id}`}>
                      <div
                        className="amo-feed-avatar"
                        style={{ background: getCommentAuthorColor(entry) }}
                        aria-hidden="true"
                      >
                        {getManagerInitials(getCommentAuthorName(entry), entry.expand?.userId?.email)}
                      </div>
                      <div className="amo-feed-bubble">
                        <div className="amo-feed-meta">
                          <span className="amo-feed-author" style={{ color: getCommentAuthorColor(entry) }}>
                            {getCommentAuthorName(entry)}
                          </span>
                          <span className="amo-feed-date">{formatShortDateTime(entry.created)}</span>
                        </div>
                        <div className="amo-feed-text">{entry.text}</div>
                      </div>
                    </div>
                  );
                }

                const assignment = item.assignment;
                const ownerName = assignment.expand?.ownerId?.name ?? '';
                const ownerEmail = assignment.expand?.ownerId?.email;
                const ownerId = assignment.expand?.ownerId?.id ?? assignment.ownerId ?? '';
                const authorName = assignment.expand?.authorId?.name ?? '';
                const authorEmail = assignment.expand?.authorId?.email;
                const authorId = assignment.expand?.authorId?.id ?? assignment.authorId ?? '';
                const isOverdue = !assignment.result && isAssignmentExpired(assignment.planDate, assignments.nowTs);
                const isOverdueOpen = assignments.overdueOpenId === assignment.id;

                return (
                  <div
                    className="amo-feed-item"
                    key={`t-${assignment.id}`}
                    style={isOverdue ? { cursor: 'pointer' } : undefined}
                    onClick={isOverdue ? () => assignments.setOverdueOpenId(prev => prev === assignment.id ? null : assignment.id) : undefined}
                  >
                    <div className={`amo-feed-task-check${isOverdue ? ' amo-feed-task-check-overdue' : ''}`} aria-hidden="true">
                      {isOverdue ? <IconX size={11} stroke={3} /> : <IconCheck size={11} stroke={3} />}
                    </div>
                    <div className={`amo-feed-bubble${isOverdue ? ' amo-feed-bubble-overdue' : ' amo-feed-bubble-task'}`}>
                      <div className="amo-feed-meta">
                        <span className={isOverdue ? 'amo-feed-overdue-label' : 'amo-feed-task-label'}>
                          {isOverdue ? 'Просрочена' : 'Задача выполнена'}
                        </span>
                        {isOverdue ? (
                          <span className="amo-feed-date">{formatShortDateTime(assignment.planDate)}</span>
                        ) : (
                          <span className="amo-feed-date">
                            План: {formatShortDateTime(assignment.planDate)} · Создана: {formatShortDateTime(assignment.created)}
                          </span>
                        )}
                      </div>
                      <div className="amo-feed-task-body">
                        <span className="amo-feed-task-name">{assignment.name}</span>
                        {assignment.result && (
                          <>
                            <IconArrowRight size={11} stroke={2} className="amo-feed-task-body-arrow" />
                            <span className="amo-feed-task-result">{assignment.result}</span>
                          </>
                        )}
                      </div>
                      <div className="amo-feed-task-assign">
                        {assignment.expand?.authorId && (
                          <div className="amo-feed-task-assign-user">
                            <div className="amo-feed-avatar amo-feed-avatar-sm" style={{ background: getManagerColor(authorId) }}>
                              {getManagerInitials(authorName, authorEmail)}
                            </div>
                            <span>{authorName}</span>
                          </div>
                        )}
                        {assignment.expand?.authorId && assignment.expand?.ownerId && (
                          <IconArrowRight size={11} stroke={2} className="amo-feed-task-assign-arrow" />
                        )}
                        {assignment.expand?.ownerId && (
                          <div className="amo-feed-task-assign-user">
                            <div className="amo-feed-avatar amo-feed-avatar-sm" style={{ background: getManagerColor(ownerId) }}>
                              {getManagerInitials(ownerName, ownerEmail)}
                            </div>
                            <span>{ownerName}</span>
                          </div>
                        )}
                        <span className="amo-feed-task-assign-date">Обновлено: {formatShortDateTime(assignment.updated)}</span>
                      </div>
                      {isOverdue && isOverdueOpen && (
                        <div className="amo-task-resolve" onClick={e => e.stopPropagation()}>
                          <textarea
                            className="amo-task-resolve-input"
                            name={`overdue-result-${assignment.id}`}
                            placeholder="Результат выполнения..."
                            value={assignments.overdueResolveTexts[assignment.id] ?? ''}
                            onChange={e => assignments.setOverdueResolveTexts(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                            rows={2}
                            autoFocus
                          />
                          <button
                            className="amo-task-resolve-btn"
                            type="button"
                            disabled={assignments.overdueResolvingId === assignment.id || !(assignments.overdueResolveTexts[assignment.id] ?? '').trim()}
                            onClick={() => assignments.handleResolveOverdue(assignment.id)}
                          >
                            {assignments.overdueResolvingId === assignment.id
                              ? <IconLoader2 size={13} stroke={2} className="spin" />
                              : <IconCheck size={13} stroke={2.5} />}
                            Выполнить
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <AssignmentsSection
              assignments={assignments.pendingAssignments}
              isLoading={assignments.isAssignmentLoading}
              prefill={pinPrefill}
              onResolve={assignments.handleResolveAssignment}
              onCreate={assignments.handleAssignmentCreate}
            />
          </div>
        </div>

        <div className="amo-compose">
          <textarea
            className="amo-compose-input"
            name="lead-comment"
            placeholder="Написать комментарий..."
            value={comment}
            onChange={e => onCommentChange(e.target.value)}
          />
          <button className="amo-compose-btn" onClick={onAddComment} type="button" disabled={!comment.trim()}>
            Добавить комментарий
          </button>
        </div>
      </div>

      <CardModalHistoryPane actions={actions} smsLogs={smsLogs} />
    </div>
  );
};

export { CardModalFeedPane };
